import React from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LyricsIcon from '@mui/icons-material/Lyrics';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import type { Song } from '../types';
import { storage } from '../lib/api';
import { usePlayer } from '../lib/PlayerContext';
import { Link, useNavigate } from 'react-router-dom';

interface SongCardProps {
  song: Song;
  artist: string;
  voicebank: string;
  layout?: 'grid' | 'list';
}

export function SongCard({ song, artist, voicebank, layout = 'grid' }: SongCardProps) {
  const player = usePlayer();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = React.useState(() => {
    const favorites = storage.getFavorites();
    return favorites.includes(song.id);
  });

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    const favorites = storage.getFavorites();
    const newFavorites = isFavorite
      ? favorites.filter(id => id !== song.id)
      : [...favorites, song.id];
    storage.setFavorites(newFavorites);
    setIsFavorite(!isFavorite);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/song/${song.id}`);
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    player.playSong(song);
  };

  if (layout === 'list') {
    return (
      <div 
        onClick={handleClick}
        className="flex items-center p-4 bg-surface-dark hover:bg-surface-raised cursor-pointer rounded-lg transition-all duration-300 hover:scale-[1.01] elevation-1 hover:elevation-2 border border-surface-border hover:border-naia-pink/30 clickable"
      >
        <div className="relative flex-shrink-0">
          <img 
            src={song.coverUrl} 
            alt={song.title}
            className="w-12 h-12 rounded-lg object-cover ring-1 ring-surface-border"
          />
          {!song.isOriginal && (
            <span className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-gradient-to-r from-naia-pink to-pink-light rounded-full text-[10px] font-medium text-pearl-white elevation-2">
              Cover
            </span>
          )}
        </div>
        <div className="ml-4 flex-grow min-w-0">
          <h3 className="font-outfit text-text-primary text-lg font-medium truncate mb-1">{song.title}</h3>
          <p className="text-text-secondary text-sm truncate">
            <Link 
              to={`/artist/${song.artistId}`}
              className="hover:text-naia-pink transition-colors"
              onClick={e => e.stopPropagation()}
            >
              {artist}
            </Link>
            {' • '}
            {voicebank && (
              <>
                <Link 
                  to={`/voicebank/${song.synthId}`}
                  className="text-text-muted hover:text-cyan-accent transition-colors"
                  onClick={e => e.stopPropagation()}
                >
                  {voicebank}
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handlePlayClick}
            className="p-2 hover:bg-naia-pink/20 rounded-full transition-all duration-300 hover:scale-110 text-naia-pink elevation-1 hover:elevation-2 clickable"
            aria-label="Play song"
          >
            <PlayArrowIcon />
          </button>
          {song.lrcUrl && (
            <div className="text-text-muted hover:text-cyan-accent transition-colors">
              <LyricsIcon fontSize="small" />
            </div>
          )}
          {song.buyUrl && (
            <a
              href={song.buyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="p-2 hover:bg-surface-raised rounded-full transition-all duration-300 hover:scale-110 text-text-muted hover:text-naia-pink clickable"
              aria-label="Buy song"
            >
              <ShoppingCartIcon />
            </a>
          )}
          <button
            onClick={toggleFavorite}
            className="p-2 hover:bg-surface-raised rounded-full transition-all duration-300 hover:scale-110 clickable"
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            {isFavorite ? (
              <FavoriteIcon className="text-naia-pink naia-glow" />
            ) : (
              <FavoriteBorderIcon className="text-text-muted hover:text-naia-pink" />
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={handleClick}
      className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-2 elevation-2 hover:elevation-4 border border-surface-border hover:border-naia-pink/50 bg-surface-dark hover:bg-surface-raised animate-scale-in"
    >
      <img 
        src={song.coverUrl} 
        alt={song.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-true-black/90 via-true-black/40 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />
      
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-t from-naia-pink/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0 pr-2">
            <h3 className="font-outfit text-text-primary text-lg font-medium truncate drop-shadow-md mb-1">
              {song.title}
            </h3>
            <p className="text-text-secondary text-sm truncate transform opacity-70 group-hover:opacity-100 transition-all duration-300 delay-100">
              <Link 
                to={`/artist/${song.artistId}`}
                className="hover:text-naia-pink transition-colors"
                onClick={e => e.stopPropagation()}
              >
                {artist}
              </Link>
              {voicebank && (
                <>
                  {' • '}
                  <Link 
                    to={`/voicebank/${song.synthId}`}
                    className="text-text-muted hover:text-cyan-accent transition-colors"
                    onClick={e => e.stopPropagation()}
                  >
                    {voicebank}
                  </Link>
                </>
              )}
            </p>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={handlePlayClick}
              className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-naia-pink to-pink-light rounded-full transition-all duration-300 hover:scale-110 opacity-0 group-hover:opacity-100 elevation-2 hover:elevation-3"
              aria-label="Play song"
            >
              <PlayArrowIcon fontSize="small" className="text-pearl-white ml-0.5" />
            </button>
            
            {song.lrcUrl && (
              <div className="text-text-muted group-hover:text-cyan-accent transition-colors p-1">
                <LyricsIcon fontSize="small" />
              </div>
            )}
            
            {song.buyUrl && (
              <a
                href={song.buyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="p-1 hover:bg-white/10 rounded-full transition-all duration-300 hover:scale-110 text-text-muted hover:text-naia-pink opacity-0 group-hover:opacity-100"
                aria-label="Buy song"
              >
                <ShoppingCartIcon fontSize="small" />
              </a>
            )}
          </div>
        </div>
      </div>
      
      {/* Favorite button */}
      <button
        onClick={toggleFavorite}
        className="absolute top-3 right-3 p-2 bg-surface-dark/60 backdrop-blur-md rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 hover:bg-surface-dark/80 elevation-1 hover:elevation-2 clickable"
        aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
      >
        {isFavorite ? (
          <FavoriteIcon className="text-naia-pink naia-glow" />
        ) : (
          <FavoriteBorderIcon className="text-text-primary hover:text-naia-pink" />
        )}
      </button>
      
      {/* Cover badge */}
      {!song.isOriginal && (
        <span className="absolute top-3 left-3 px-2 py-1 bg-gradient-to-r from-naia-pink to-pink-light rounded-full text-xs font-medium text-pearl-white elevation-2 animate-fade-in">
          Cover
        </span>
      )}
    </div>
  );
} 